export interface AddressComponents {
  street?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
}

export function parseAddress(address: string): AddressComponents {
  if (!address) return {};

  const components: AddressComponents = {};
  
  // Split by commas and trim
  const parts = address.split(',').map(part => part.trim()).filter(Boolean);
  
  if (parts.length === 0) return components;

  // Common patterns
  const postalCodePatterns = [
    /\b[A-Z]\d{1,2}\s?\d[A-Z]{2}\b/i, // UK: SW1A 1AA
    /\b[A-Z]\d{2}\s?[A-Z0-9]{4}\b/i, // Ireland: D01 TR53
    /\b\d{5}(-\d{4})?\b/, // US: 12345 or 12345-6789
    /\b\d{4,6}\b/, // Generic: 12345
  ];

  const knownCountries = [
    'Ireland', 'United Kingdom', 'UK', 'United States', 'USA',
    'Canada', 'Australia', 'France', 'Germany', 'Spain', 'Italy'
  ];

  // Extract postal code (process last to first to avoid matching street numbers)
  for (let i = parts.length - 1; i >= 0; i--) {
    let matched = false;
    for (const pattern of postalCodePatterns) {
      const match = parts[i].match(pattern);
      if (match) {
        // Only match if it's not at the start of a hyphenated street number
        const beforeMatch = parts[i].substring(0, match.index);
        const isHyphenatedStreetNumber = beforeMatch.match(/\d+-$/);
        
        if (!isHyphenatedStreetNumber) {
          components.postalCode = match[0];
          // Remove postal code from the part
          parts[i] = parts[i].replace(match[0], '').trim();
          matched = true;
          break;
        }
      }
    }
    if (matched) break;
  }

  // Extract country (usually last part)
  const lastPart = parts[parts.length - 1];
  const countryMatch = knownCountries.find(c => 
    lastPart?.toLowerCase().includes(c.toLowerCase())
  );
  if (countryMatch) {
    components.country = lastPart;
    parts.pop(); // Remove from parts
  }

  // Remaining parts
  const remaining = parts.filter(p => p.length > 0);

  if (remaining.length >= 3) {
    // Format: street, area, city
    components.street = remaining[0];
    components.region = remaining[1];
    components.city = remaining[2];
  } else if (remaining.length === 2) {
    // Format: street, city
    components.street = remaining[0];
    components.city = remaining[1];
  } else if (remaining.length === 1) {
    // Just street or city
    components.street = remaining[0];
  }

  // Clean up empty strings
  Object.keys(components).forEach(key => {
    if (!components[key as keyof AddressComponents]) {
      delete components[key as keyof AddressComponents];
    }
  });

  return components;
}
